import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_TERMS, newLabel, type Permission, type Term, type TermKey, type Terms } from '@planning/contracts';
import { avisar } from '@xiltrion/ui/sonner';
import { adoptTenant, apiGet, apiPost, ApiError, markLeaving, onUnauthorized } from '@/api/client';
import type { Me } from '@/api/types';
import { startActivityTracking } from './activity';
import { applyTheme } from './theme';

interface Session {
  me: Me | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  admin: boolean;
  /** ¿El módulo está activo para el tenant? */
  has: (module: string) => boolean;
  terms: Terms;
  /** Cierra la sesión y va al cierre de la suite; si no se pudo, avisa y rechaza (el diálogo sigue abierto). */
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<Session>({ me: null, loading: true, can: () => false, admin: false, has: () => false, terms: DEFAULT_TERMS, logout: async () => undefined, refresh: async () => undefined });

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await apiGet<Me>('/me');
        adoptTenant(me.tenant.slug);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  const end = useCallback(() => {
    // Se vacía lo de la persona, no lo que la pantalla de ingreso necesita (`providers`): el 401 de `/me` llega
    // mientras esa pantalla ya pidió los proveedores y, si se borrara, se quedaría sin el botón de la suite.
    qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' && query.queryKey[0] !== 'providers' });
    qc.setQueryData(['me'], null);
  }, [qc]);
  useEffect(() => startActivityTracking(), []);
  useEffect(() => onUnauthorized(() => end()), [end]);
  useEffect(() => {
    if (me?.preferences.theme) applyTheme(me.preferences.theme);
  }, [me?.preferences.theme]);
  const value: Session = {
    me,
    loading: q.isLoading,
    can: (p) => !!me?.permissions.includes(p),
    admin: !!me?.permissions.includes('admin.manage'),
    has: (m) => !!me?.modules.includes(m),
    terms: me?.terms ?? DEFAULT_TERMS,
    logout: async () => {
      // GUIA §3.1 «Salir»: desde aquí ningún 401 manda a ingresar, y la página va al `end_session_endpoint` de la
      // suite, que cierra el SSO y las demás apps y aterriza en la portada. Nunca a `/ingreso` ni a `…/oidc/start`:
      // con el SSO vivo volvería a entrar sola y «Salir» parecería no hacer nada.
      markLeaving();
      try {
        const r = await apiPost<{ redirect?: string } | undefined>('/auth/logout');
        if (!r?.redirect) throw new Error('El API no devolvió el cierre de sesión de la suite');
        window.location.assign(r.redirect);
      } catch (e) {
        markLeaving(false);
        avisar.error('No se pudo cerrar la sesión. Intenta de nuevo.');
        throw e;
      }
    },
    refresh: () => q.refetch(),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);

/** Vocabulario del tenant (spec §4.4): `t('goal')` → «Objetivo» o «Meta». */
export function useTerms() {
  const { terms } = useSession();
  const t = (k: TermKey, plural = false) => (plural ? terms[k].many : terms[k].one);
  const nuevo = (k: TermKey) => newLabel(terms[k] as Term);
  const lower = (k: TermKey, plural = false) => t(k, plural).toLowerCase();
  return { t, nuevo, lower, terms };
}
